import { platform } from "../platform/index.js";
// Stooq — a free, keyless, genuinely INDEPENDENT source (not Yahoo data
// under a different hostname). Daily/weekly bars only, but it covers US
// stocks, indices, and continuous futures (gc.f = COMEX gold), which
// makes it the fallback that keeps daily charts alive if Yahoo breaks —
// the audit's exact worry about the unofficial-endpoint dependency.

import { MarketDataError, type OHLCPoint } from "../market-data.js";
import { rangeToMs } from "./types.js";

/** Our tickers → Stooq symbols. Pure. Returns null when unmapped. */
export function stooqSymbol(symbol: string): string | null {
  const s = symbol.trim().toUpperCase();
  if (s.endsWith("=F")) return `${s.slice(0, -2).toLowerCase()}.f`; // GC=F → gc.f (continuous)
  if (s.endsWith("=X")) {
    const pair = s.slice(0, -2).toLowerCase(); // EURUSD=X → eurusd
    return pair.length === 6 ? pair : null;
  }
  const indexMap: Record<string, string> = { "^GSPC": "^spx", "^DJI": "^dji", "^IXIC": "^ndq" };
  if (s.startsWith("^")) return indexMap[s] ?? null;
  if (/^[A-Z.]{1,6}$/.test(s)) return `${s.toLowerCase()}.us`; // US equities/ETFs
  return null; // crypto etc. — other sources own those
}

/** Only daily/weekly are served. Pure. */
export function stooqInterval(interval: string): "d" | "w" | null {
  if (interval === "1d") return "d";
  if (interval === "1wk") return "w";
  return null;
}

export async function stooqCandles(symbol: string, range: string, interval: string): Promise<{ candles: OHLCPoint[]; currentPrice?: number }> {
  const mapped = stooqSymbol(symbol);
  const i = stooqInterval(interval);
  if (!mapped || !i) throw new MarketDataError(`Stooq can't serve ${symbol} @ ${interval}`);

  const url = `https://stooq.com/q/d/l/?s=${encodeURIComponent(mapped)}&i=${i}`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await platform.dataFetch(url, { signal: controller.signal });
    if (!response.ok) throw new MarketDataError(`Stooq request failed: ${response.status}`);
    const csv = await response.text();
    const cutoff = Date.now() - rangeToMs(range);
    const candles: OHLCPoint[] = [];
    for (const line of csv.split("\n").slice(1)) {
      // Date,Open,High,Low,Close,Volume
      const [date, open, high, low, close, volume] = line.trim().split(",");
      if (!date || !close) continue;
      const at = new Date(`${date}T00:00:00`);
      if (Number.isNaN(at.getTime()) || at.getTime() < cutoff) continue;
      candles.push({
        date: at,
        open: Number(open),
        high: Number(high),
        low: Number(low),
        close: Number(close),
        volume: Number(volume ?? 0) || 0,
      });
    }
    if (!candles.length) throw new MarketDataError("Stooq returned no rows");
    const last = candles[candles.length - 1];
    return { candles, currentPrice: last?.close };
  } finally {
    clearTimeout(timeout);
  }
}
